import { create } from "zustand"
import { immer } from "zustand/middleware/immer"
import { z } from "zod"
import { httpClient, apiUrls } from "@/src/api"
import type { AppConfig, WhatsappConnectionStatus } from "@/src/types/appConfig"
import type { AppConfigStore } from "../types"

// Zod schemas for validation
const WhatsappConnectionStatusSchema = z.enum([
    "WHITELISTED",
    "NOT_WHITELISTED",
    "CONNECTING",
    "CONNECTED",
    "FAILED",
    "REAUTHENTICATION_REQUIRED",
    "PENDING",
])

const AppConfigSchema = z.object({
    workspacesEnabled: z.boolean(),
})

export const useAppConfigStore = create<AppConfigStore>()(
    immer((set, get) => ({
        // State
        appConfig: null,
        loading: true,
        error: null,

        // Actions
        fetchConfig: async () => {
            set((state) => {
                state.loading = true
                state.error = null
            })

            try {
                const res = await httpClient.get(apiUrls.APP_CONFIG())
                const configData = res.data?.data

                // Config data validation can be added later if needed
                // Store the entire config object from server
                const config: AppConfig = {
                    workspacesEnabled: configData.workspacesEnabled,
                    ...configData, // Spread all other config properties
                }

                set((state) => {
                    state.appConfig = config
                    state.loading = false
                    state.error = null
                })
            } catch (error) {
                console.error("Failed to fetch app config:", error)
                set((state) => {
                    state.appConfig = null
                    state.loading = false
                    state.error = error instanceof Error ? error.message : "Failed to fetch app config"
                })
            }
        },

        setWhatsappConnectionStatus: (status: WhatsappConnectionStatus) => {
            set((state) => {
                if (state.appConfig) {
                    // Update the app config with new WhatsApp status
                    // Note: This is a placeholder - actual implementation depends on backend structure
                    state.appConfig = { ...state.appConfig }
                }
                state.error = null
            })
        },
    }))
)
